import { CustomAttr, Service, Business } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useMemo } from 'react';
import { Modal, Button, TextInput, Switch, ActionIcon, Autocomplete, Loader, Select } from '@mantine/core';
import { IconEdit, IconX, IconPlus, IconWand, IconTrash } from '@tabler/icons-react';
import { ALL_TEMPLATES } from '@/types/templates';
import { TimePicker } from '@mantine/dates';
import { formatTime } from '@/lib/format-utils';
import { useDynamicOptions } from '@/lib/dynamic-options/attr-dynamic-options';

interface Props {
    attrs: CustomAttr[];
    attrType: 'self_attrs' | 'booking_attrs';
    item: any; //context
    onChange: (attrs: CustomAttr[]) => void;
    isSaving?: boolean;
    title?: string;
    subtitle?: string;
}

export default function EditAttrsModal({ attrs, item, onChange, isSaving = false, title, attrType, subtitle }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [editingAttrs, setEditingAttrs] = useState<CustomAttr[]>([]);
    const [focusedAttr, setFocusedAttr] = useState<CustomAttr | null>(null);
    const [newAttrKey, setNewAttrKey] = useState('');

    const {
        data: suggestions,
        isLoading: isLoadingSuggestions
    } = useDynamicOptions(
        focusedAttr,
        item
    );

    const hasChanges = useMemo(() => {
        return JSON.stringify(attrs) !== JSON.stringify(editingAttrs);
    }, [attrs, editingAttrs]);

    const resetToTemplate = () => {
        const allTemplateAttrs = [...attrs];
        // Add missing template attributes
        ALL_TEMPLATES[item?.category]?.[attrType]?.forEach(attr => {
            if (!allTemplateAttrs.some(a => a.key === attr.key)) {
                allTemplateAttrs.push(attr);
            }
        });
        setEditingAttrs(allTemplateAttrs);
    };

    const handleOpen = () => {
        setEditingAttrs([...attrs]);
        setOpened(true);
    };

    const handleClose = () => {
        setOpened(false);
        setNewAttrKey('');
        setEditingAttrs([...attrs]); // Reset to original
    };

    const handleSave = () => {
        onChange(editingAttrs);
        handleClose();
    };

    const handleAttrChange = useCallback((index: number, key: keyof CustomAttr, value: any) => {
        const newAttrs = [...editingAttrs];
        newAttrs[index] = { ...newAttrs[index], [key]: value };
        setEditingAttrs(newAttrs);
    }, [editingAttrs]);

    const handleRemoveAttr = useCallback((index: number) => {
        const newAttrs = editingAttrs.filter((_, i) => i !== index);
        setEditingAttrs(newAttrs);
    }, [editingAttrs]);

    const handleAddAttr = () => {
        if (newAttrKey.trim()) {
            const newAttr: CustomAttr = {
                key: newAttrKey.trim(),
                type: 'text',
                value: null,
                section: 'custom_99',
                sort: editingAttrs.length + 1
            };
            setEditingAttrs([...editingAttrs, newAttr]);
            setNewAttrKey('');
        }
    };

    const formatAttrValue = (attr: CustomAttr) => {
        if (attr.value === null || attr.value === undefined) return '';
        if (attr.type === 'boolean') return attr.value ? t('yes') : t('no');
        if (attr.type === 'time') return formatTime(attr.value);
        if (Array.isArray(attr.value)) return attr.value.join(', ');
        return t(String(attr.value));
    };

    const groupedAttrs = editingAttrs.reduce((groups, attr, idx) => {
        const section = (attr.section || 'custom_99').split('_')[0];
        const sortOrder = parseInt((attr.section || 'custom_99').split('_')[1]) || 99;
        if (!groups[section]) {
            groups[section] = { items: [], sortOrder };
        }
        groups[section].items.push({ attr, idx });
        return groups;
    }, {} as Record<string, { items: { attr: CustomAttr; idx: number }[]; sortOrder: number }>);

    const sortedGroups = Object.entries(groupedAttrs).sort((a, b) => {
        return b[1].sortOrder - a[1].sortOrder;
    });

    const getUniqueOptions = useCallback((optionsList: string[], suggestionsList: any[]) => {
        const seen = new Set<string>();
        const result: string[] = [];
        const combined = [
            ...(optionsList || []),
            ...(suggestionsList || []).map(s => typeof s === 'string' ? s : s?.value)
        ];
        for (const item of combined) {
            if (!item) continue;
            const trimmed = typeof item === 'string' ? item.trim() : String(item).trim();
            if (!trimmed) continue;
            const lower = trimmed.toLowerCase();
            if (!seen.has(lower)) {
                seen.add(lower);
                result.push(trimmed);
            }
        }
        return result;
    }, []);

    return (
        <>
            <div className="solid-box-border w-full md:w-auto">
                <div className="flex gap-4 items-center justify-between mb-4">
                    <div className="text-md font-bold">
                        {title || ''}
                        {subtitle && (
                            <div className="text-xs font-normal text-gray-500">
                                {subtitle}
                            </div>
                        )}
                    </div>
                    <Button
                        size="md"
                        color="black"
                        variant='subtle'
                        onClick={handleOpen}
                        loading={isSaving}
                    >
                        <IconEdit size={20} />
                    </Button>
                </div>
                <div className="text-sm space-y-1">
                    {attrs?.map((attr, index) => (
                        <div
                            key={`display-${index}-${attr.key}`}
                            className="flex gap-4 justify-between items-center solid-box-border-tinted"
                        >
                            <div className="font-bold text-gray-400">
                                {t(attr.key)}
                            </div>
                            <div className="font-bold truncate">
                                {formatAttrValue(attr)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <Modal
                opened={opened}
                onClose={handleClose}
                title={
                    <div className="text-lg font-semibold">
                        {t('edit')} {title || ''}
                    </div>
                }
                size="lg"
                radius="xl"
                centered
                padding="xl"
                styles={{
                    body: { paddingBottom: 0 },
                    content: { paddingBottom: 0 }
                }}
            >
                <div className="space-y-2">
                    {sortedGroups.length === 0 ? (
                        null
                    ) : (
                        sortedGroups.map(([section, group]) => (
                            <div
                                key={section}
                                className="subtle-box"
                            >
                                <div className="space-y-3">
                                    {group.items.sort((a, b) => (a.attr.sort || 0) - (b.attr.sort || 0)).map(({ attr, idx }) => (
                                        <div key={`${section}-${idx}-${attr.key}`} className="group">
                                            <div className="flex items-center gap-1">
                                                <div className="w-40">
                                                    <span className="text-sm font-normal text-gray-800">
                                                        {t(attr.key)}
                                                    </span>
                                                </div>

                                                {attr.type === 'boolean' ? (
                                                    <div className="flex-1 flex items-center">
                                                        <Switch
                                                            checked={Boolean(attr.value) || false}
                                                            onChange={(e) => handleAttrChange(idx, 'value', e.currentTarget.checked)}
                                                            size="sm"
                                                        />
                                                    </div>
                                                ) : attr.type === 'time' ? (
                                                    <div className="flex-1">
                                                        <TimePicker
                                                            value={attr.value || ''}
                                                            onChange={(value) => handleAttrChange(idx, 'value', value)}
                                                        />
                                                    </div>
                                                ) : attr.type === 'number' ? (
                                                    <TextInput
                                                        className="flex-1"
                                                        type="number"
                                                        size="sm"
                                                        value={attr.value || ''}
                                                        onChange={(e) => handleAttrChange(idx, 'value', e.target.value ? Number(e.target.value) : null)}

                                                    />
                                                ) : attr.options && attr.options.length > 0 ? (
                                                    <Select
                                                        className="flex-1"
                                                        size="sm"
                                                        searchable
                                                        value={attr.value || ''}
                                                        data={attr.options.map(o => ({ value: o, label: t(o) }))}
                                                        onChange={(val) => handleAttrChange(idx, 'value', val)}
                                                        placeholder={attr.placeholder || t('select_option')}
                                                    />
                                                ) : (
                                                    <Autocomplete
                                                        className="flex-1"
                                                        size="sm"
                                                        value={attr.value || ''}
                                                        onFocus={() => setFocusedAttr(attr)}
                                                        onBlur={() => setFocusedAttr(null)}
                                                        data={getUniqueOptions(attr?.options || [], suggestions || [])}
                                                        onChange={(str) => handleAttrChange(idx, 'value', str)}
                                                        placeholder={attr.placeholder}
                                                        rightSection={
                                                            focusedAttr?.key === attr.key && isLoadingSuggestions ? (
                                                                <Loader size="xs" />
                                                            ) : undefined
                                                        }
                                                    />
                                                )}

                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    onClick={() => handleRemoveAttr(idx)}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <IconTrash size={16} stroke={1} />
                                                </ActionIcon>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="solid-box-border-dark my-2">
                    <div className="text-xs font-bold mb-4">
                        {t('add_new_attribute')}
                    </div>
                    <div className="flex gap-2">
                        <TextInput
                            className="flex-1"
                            value={newAttrKey}
                            onChange={(e) => setNewAttrKey(e.target.value)}
                            placeholder={t('attribute_name')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && newAttrKey.trim()) handleAddAttr();
                            }}
                        />
                        <Button
                            size="xs"
                            radius="xl"
                            color="black"
                            leftSection={<IconPlus size={18} />}
                            onClick={handleAddAttr}
                            disabled={!newAttrKey.trim()}
                        >
                            {t('add')}
                        </Button>
                    </div>
                </div>

                <div className="flex justify-between items-center py-8">
                    <div className="flex gap-4">
                        <Button
                            variant="subtle"
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={handleClose}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="subtle"
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={resetToTemplate}
                        >
                            {t('reset_to_template')}
                        </Button>
                    </div>
                    <Button
                        size="xs"
                        radius="xl"
                        onClick={handleSave}
                        color="black"
                        disabled={!hasChanges}
                    >
                        {t('save_changes')}
                    </Button>
                </div>
            </Modal>
        </>
    );
}